import capitalize from "lodash/capitalize";
import get from "lodash/get";

import type { ISwSettings } from "app/@types/ISwSettings";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import type { IWidgetModel } from "app/components/widget/widget-types/Widget";
import { AppStoreName } from "app/pages/app-intelligence/constants";
import { getHasPremiumPlusClaim } from "app/pages/app-intelligence/helpers";

import { RankCell } from "../../../React/Table/cells";

const getShouldHideMetric = ({ user }: ISwSettings) => {
    return !user.hasSwAppIntelligence;
};

export const AppEngagementCurrentInstalls = {
    id: "AppEngagementCurrentInstalls",
    properties: {
        dashboard: "true",
        title: "metric.AppEngagementCurrentInstalls.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        order: "1",
        androidOnly: "true",
        state: "digitalsuite_app_usersengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementStoreOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Current Installs",
                    type: "double?",
                    format: "smallNumbersPercentage",
                    formatParameter: 3,
                    name: "CurrentInstalls",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Current Installs",
                    type: "double?",
                    format: "smallNumbersPercentage",
                    formatParameter: 3,
                    name: "CurrentInstalls",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppEngagementDownloads = {
    id: "AppEngagementDownloads",
    properties: {
        dashboard: "true",
        title: "apps.engagementoverview.tabs.downloads.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        order: "1",
        componentFunction: <IAutoCompleteAppItem>({ key }) =>
            key && key.length > 0 && key[0].store !== "google"
                ? "AppEngagementOverviewIOS"
                : "AppEngagementOverviewAndroid",
        configIdDeps: ["AppEngagementOverviewIOS", "AppEngagementOverviewAndroid"],
        state: "digitalsuite_app_storeengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementStoreOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: (swSettings: ISwSettings) => getShouldHideMetric(swSettings) || getHasPremiumPlusClaim(),
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.downloads.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "Downloads",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.downloads.title",
                    type: "double?",
                    format: "abbrNumber",
                    name: "Downloads",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppEngagementUniqueInstalls = {
    id: "AppEngagementUniqueInstalls",
    properties: {
        dashboard: "true",
        title: "apps.engagementoverview.tabs.unique.installs.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        androidOnly: "true",
        order: "4",
        componentFunction: <IAutoCompleteAppItem>({ key }) => "AppEngagementOverviewAndroid",
        configIdDeps: ["AppEngagementOverviewAndroid"],
        state: "digitalsuite_app_storeengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementStoreOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.unique.installs.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "New devices",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.unique.installs.title",
                    type: "double?",
                    format: "abbrNumber",
                    name: "New Devices",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppEngagementDailyActiveUsers = {
    id: "AppEngagementDailyActiveUsers",
    properties: {
        dashboard: "true",
        title: "metric.AppEngagementDailyActiveUsers.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        state: "digitalsuite_app_usersengagement",
        androidOnly: "true",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementUserOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "DailyActiveUsers",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "DailyActiveUsers",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppRetention = {
    id: "AppRetention",
    properties: {
        dashboard: "true",
        title: "metric.appRetention.title",
        family: "Mobile",
        component: "AppRetentionFront",
        androidOnly: "true",
        state: "digitalsuite_app_appretention",
        apiParams: {
            device: "AndroidPhone",
        },
        order: "2",
        apiController: "AppRetention",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppRetentionDashboardGraph",
            AppRetentionDashboardGraph: {
                properties: {
                    width: 12,
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Day",
                    type: "category",
                    format: "None",
                    name: "Day",
                    reversed: "False",
                },
                y_axis: {
                    title: "Retention Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
            AppRetentionDashboardTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        desktopOnly: true,
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                filters: {},
            },
        },
        compare: {
            defaultType: "AppRetentionDashboardGraph",
            AppRetentionDashboardGraph: {
                properties: {
                    width: 4,
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Day",
                    type: "category",
                    format: "None",
                    name: "Day",
                    reversed: "False",
                },
                y_axis: {
                    title: "Retention Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
            AppRetentionDashboardTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        desktopOnly: true,
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                filters: {},
            },
        },
    },
};

export const AppEngagementMonthlyActiveUsers = {
    id: "AppEngagementMonthlyActiveUsers",
    properties: {
        dashboard: "true",
        title: "metric.AppEngagementMonthlyActiveUsers.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        componentFunction: <IAutoCompleteAppItem>({ key }) =>
            key && key.length > 0 && key[0].store !== "google"
                ? "AppEngagementOverviewIOS"
                : "AppEngagementOverviewAndroid",
        configIdDeps: ["AppEngagementOverviewIOS", "AppEngagementOverviewAndroid"],
        state: "digitalsuite_app_usersengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementUserOverview",
            granularity: "Monthly",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    periodOverPeriodSupport: true,
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "MonthlyActiveUsers",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "MonthlyActiveUsers",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppUsersEngagementAvgSessionsPerUser = {
    id: "AppUsersEngagementAvgSessionsPerUser",
    properties: {
        dashboard: "true",
        title: "metric.AppUsersEngagementAvgSessionsPerUser.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        state: "digitalsuite_app_usersengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementUserOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "AvgSessionsPerUser",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "abbrNumberVisits",
                    name: "AvgSessionsPerUser",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppUsersEngagementAvgUsageTimePerSession = {
    id: "AppUsersEngagementAvgUsageTimePerSession",
    properties: {
        dashboard: "true",
        title: "metric.AppUsersEngagementAvgUsageTimePerSession.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        state: "digitalsuite_app_usersengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementUserOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "time",
                    name: "AvgUsageTimePerSession",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "time",
                    name: "AvgUsageTimePerSession",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppUsersEngagementAvgTotalUsageTimePerUser = {
    id: "AppUsersEngagementAvgTotalUsageTimePerUser",
    properties: {
        dashboard: "true",
        title: "metric.AppUsersEngagementAvgTotalUsageTimePerUser.title",
        family: "Mobile",
        component: "AppEngagementOverview",
        state: "digitalsuite_app_usersengagement",
        apiController: "AppEngagementOverview",
        apiParams: {
            metric: "AppEngagementUserOverview",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "time",
                    name: "AvgTotalUsageTimePerUser",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "apps.engagementoverview.tabs.activeusers.title",
                    type: "double?",
                    format: "time",
                    name: "AvgTotalUsageTimePerUser",
                    reversed: "False",
                },
                filters: {
                    timeGranularity: [
                        {
                            value: "Daily",
                            title: "Daily",
                        },
                        {
                            value: "Weekly",
                            title: "Weekly",
                        },
                        {
                            value: "Monthly",
                            title: "Monthly",
                        },
                    ],
                },
            },
        },
    },
};

export const AppRanks = {
    id: "AppRanks",
    properties: {
        dashboard: "true",
        title: "metric.appRanks.title",
        family: "Mobile",
        component: "AppRanksDashboard",
        order: "1",
        dynamicSettings: "true",
        state: "digitalsuite_app_apprankinghistory",
        timeGranularity: "Daily",
        apiController: "AppRanks",
        availabilityComponent: "WsAppRanking",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            Graph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Ranks",
                    type: "long",
                    format: "number",
                    name: "Rank",
                    reversed: "True",
                },
                filters: {},
            },
            SingleMetric: {
                properties: {
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                objects: {
                    Rank: {
                        name: "Rank",
                        title: "App Rank",
                        type: "int?",
                        format: "None",
                        cellTemp: "large-number",
                    },
                    Change: {
                        name: "Change",
                        title: "Rank Change",
                        type: "double?",
                        format: "None",
                        cellTemp: "change",
                    },
                    Trend: {
                        name: "Trend",
                        title: "Trend",
                        type: "long[]",
                        format: "None",
                        cellTemp: "trend-line",
                    },
                },
            },
        },
        compare: {
            Graph: {
                properties: { hideMarkersOnDaily: true },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Ranks",
                    type: "long",
                    format: "number",
                    name: "Rank",
                    reversed: "True",
                },
                filters: {},
            },
            Table: {
                properties: {
                    options: {
                        showLegend: false,
                    },
                },
                columns: [
                    {
                        name: "App",
                        title: "Mobile App",
                        type: "string",
                        format: "None",
                        sortable: "False",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "app-tooltip-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: "",
                        width: "",
                        ppt: {
                            overrideFormat: "App",
                        },
                    },
                    {
                        name: "Rank",
                        title: "Rank",
                        type: "long",
                        format: "swRank",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellComponent: RankCell,
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: "",
                        width: "",
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "int?",
                        format: "appRankChange",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: "",
                        width: "",
                    },
                ],
                filters: {},
            },
        },
    },
};

export const AppEngagementUserOverview = {
    id: "AppEngagementUserOverview",
    properties: {
        family: "Mobile",
        component: "AppEngagementOverviewAndroid",
        order: "1",
        state: "digitalsuite_app_usersengagement",
        dashboard: "true",
        androidOnly: "true",
        title: "apps.usage.usersengagement.dashboardWidget.title",
        apiController: "AppEngagementOverview",
        compareOnly: "true",
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {},
        compare: {
            defaultType: "AppEngagementOverviewTable",
            AppEngagementOverviewTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
            },
        },
    },
};

export const AppEngagementStoreOverview = {
    id: "AppEngagementStoreOverview",
    properties: {
        family: "Mobile",
        component: "AppEngagementOverviewAndroid",
        order: "1",
        state: "digitalsuite_app_storeengagement",
        dashboard: "true",
        title: "apps.store.installAndDownloads.table.header",
        apiController: "AppEngagementOverview",
        androidOnly: "true",
        compareOnly: "true",
        availabilityComponent: "WsAppEngagement",
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {},
        compare: {
            defaultType: "AppStoreInstallAndDownloadsOverviewTable",
            AppStoreInstallAndDownloadsOverviewTable: {
                properties: {
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
            },
        },
    },
};

export const AppDemographicsAge = {
    id: "AppDemographicsAge",
    properties: {
        title: "appdemographics.bar.title",
        family: "Mobile",
        component: "AppAudienceDemographics",
        state: "digitalsuite_app_appdmg",
        dashboard: "true",
        availabilityComponent: "WsAppDemographics",
        androidOnly: true,
        disableWindowInDatepicker: true,
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
        customFetcherFactory: {
            factoryName: "AppDemographicsDataFetcherFactory",
            overrideDashboardFetching: true,
        },
        apiParams: {
            demographicDataKey: "age",
        },
    },
    widgets: {
        single: {
            BarChartDemographics: {
                properties: {
                    height: 220,
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 5,
                        desktopOnly: false,
                        legendAlign: "left",
                        showTopLine: false,
                        showFrame: true,
                        showSubtitle: false,
                        showTitleTooltip: true,
                        forceSetupColors: true,
                        widgetColorsFrom: "blue",
                    },
                },
            },
            PieChart: {
                properties: {
                    height: 240,
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 25,
                        showTitleTooltip: true,
                        desktopOnly: false,
                        legendConfig: {
                            margin: 20,
                        },
                        forceSetupColors: true,
                        widgetColorsFrom: "audienceOverview",
                    },
                },
                objects: {
                    Age18To24: {
                        name: "Age18To24",
                        title: "18-24",
                        displayName: "18-24",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Age25To34: {
                        name: "Age25To34",
                        title: "25-34",
                        displayName: "25-34",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Age35To44: {
                        name: "Age35To44",
                        title: "35-44",
                        displayName: "35-44",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Age45To54: {
                        name: "Age45To54",
                        title: "45-54",
                        displayName: "45-54",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Age55Plus: {
                        name: "Age55Plus",
                        title: "55+",
                        displayName: "55+",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                },
            },
        },
        compare: {
            BarChartDemographics: {
                properties: {
                    height: 200,
                    options: {
                        height: 140,
                        dashboardSubtitleMarginBottom: 5,
                        desktopOnly: false,
                        legendAlign: "left",
                        showTopLine: false,
                        showFrame: true,
                        showSubtitle: false,
                        showTitleTooltip: true,
                        forceSetupColors: true,
                        widgetColorsFrom: "audienceOverview",
                    },
                },
            },
        },
    },
};

export const AppDemographicsGender = {
    id: "AppDemographicsGender",
    properties: {
        title: "appdemographics.piechart.title",
        family: "Mobile",
        component: "AppAudienceDemographics",
        state: "digitalsuite_app_appdmg",
        dashboard: "true",
        availabilityComponent: "WsAppDemographics",
        androidOnly: "true",
        disableWindowInDatepicker: true,
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
        customFetcherFactory: {
            factoryName: "AppDemographicsDataFetcherFactory",
            overrideDashboardFetching: true,
        },
        apiParams: {
            demographicDataKey: "gender",
        },
    },
    widgets: {
        single: {
            PieChart: {
                properties: {
                    height: 220,
                    options: {
                        twoColorMode: true,
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                        showTitleTooltip: true,
                    },
                },
                objects: {
                    Male: {
                        name: "Male",
                        title: "Male",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Female: {
                        name: "Female",
                        title: "Female",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                },
            },
        },
        compare: {
            BarChartDemographicsGender: {
                properties: {
                    height: 200,
                    options: {
                        height: 140,
                        dashboardSubtitleMarginBottom: 5,
                        desktopOnly: false,
                        legendAlign: "left",
                        showTopLine: false,
                        showFrame: true,
                        showSubtitle: false,
                        showTitleTooltip: true,
                        forceSetupColors: true,
                        widgetColorsFrom: "audienceOverview",
                        cssClass: "age-bar",
                        barWidth: 50,
                    },
                },
            },
        },
    },
};

export const AppDemographicsOverTimeGender = {
    id: "AppDemographicsOverTimeGender",
    properties: {
        dashboard: "true",
        title: "metric.appDemographics.overtime.gender.title",
        family: "Mobile",
        component: "AppAudienceDemographics",
        order: "1",
        state: "digitalsuite_app_appdmg",
        timeGranularity: "Monthly",
        endpoint: "api/AppDemographics/appsAndCategories",
        availabilityComponent: "WsAppDemographics",
        androidOnly: "true",
        disableWindowInDatepicker: true,
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppDemographicsGraph",
            AppDemographicsGraph: {
                properties: {
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Demographics Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {
                    demographicDataKey: [
                        {
                            value: "Male",
                            title: "Male",
                        },
                        {
                            value: "Female",
                            title: "Female",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppDemographicsGraph",
            AppDemographicsGraph: {
                properties: {},
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Demographics Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {
                    demographicDataKey: [
                        {
                            value: "Male",
                            title: "Male",
                        },
                        {
                            value: "Female",
                            title: "Female",
                        },
                    ],
                },
            },
        },
    },
};

export const AppDemographicsOverTimeAge = {
    id: "AppDemographicsOverTimeAge",
    properties: {
        dashboard: "true",
        title: "metric.appDemographics.overtime.age.title",
        family: "Mobile",
        component: "AppAudienceDemographics",
        order: "1",
        state: "digitalsuite_app_appdmg",
        timeGranularity: "Monthly",
        endpoint: "api/AppDemographics/appsAndCategories",
        availabilityComponent: "WsAppDemographics",
        androidOnly: "true",
        disableWindowInDatepicker: true,
        isHidden: getShouldHideMetric,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppDemographicsGraph",
            AppDemographicsGraph: {
                properties: {
                    options: {
                        showLegend: false,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Demographics Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {
                    demographicDataKey: [
                        {
                            value: "Age18To24",
                            title: "18-24",
                        },
                        {
                            value: "Age25To34",
                            title: "25-34",
                        },
                        {
                            value: "Age35To44",
                            title: "35-44",
                        },
                        {
                            value: "Age45To54",
                            title: "45-54",
                        },
                        {
                            value: "Age55Plus",
                            title: "55+",
                        },
                    ],
                },
            },
        },
        compare: {
            defaultType: "AppDemographicsGraph",
            AppDemographicsGraph: {
                properties: {},
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Demographics Over Time",
                    type: "double",
                    format: "percentagesign",
                    formatParameter: 0,
                    name: "Retention",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {
                    demographicDataKey: [
                        {
                            value: "Age18To24",
                            title: "18-24",
                        },
                        {
                            value: "Age25To34",
                            title: "25-34",
                        },
                        {
                            value: "Age35To44",
                            title: "35-44",
                        },
                        {
                            value: "Age45To54",
                            title: "45-54",
                        },
                        {
                            value: "Age55Plus",
                            title: "55+",
                        },
                    ],
                },
            },
        },
    },
};

export const AppDistribution = {
    id: "AppDistribution",
    properties: {
        width: 4,
        dashboard: "true",
        title: "dashboard.metric.app.distribution.title",
        order: "1",
        family: "Mobile",
        component: "AppAudienceInterests",
        endpoint: "api/AppEngagement/GetAudienceInterests",
        state: "digitalsuite_app_appinterests",
        androidOnly: "true",
        availabilityComponent: "WsAppAudience",
        isHidden: false,
        compareOnly: false,
        noCompare: true,
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppDistributionTable",
            AppDistributionTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                        showMoreButton: true,
                    },
                },
            },
        },
    },
};

export const AppEngagementDailyStickiness = {
    id: "AppEngagementDailyStickiness",
    properties: {
        dashboard: "true",
        title: "metric.AppEngagementDailyStickiness.title",
        family: "Mobile",
        component: "AppEngagementUserActivity",
        componentFunction: ({ key }) =>
            key && key.length > 0 && key[0].store === AppStoreName.APPLE.toLowerCase()
                ? "AppEngagementUserActivity"
                : "AppEngagementOverview",
        state: "digitalsuite_app_usersengagement",
        endpoint: (_key: string, model: IWidgetModel) => {
            const store = capitalize(get(model, "key[0].store", ""));
            return `/widgetApi/AppDashboard/DailyStickiness/${store}`;
        },
        apiParams: {
            granularity: "Monthly",
        },
        availabilityComponent: "WsAppEngagement",
        isHidden: () => !getHasPremiumPlusClaim(),
        options: {
            deviceFilteringUnavailable: true,
        },
    },
    widgets: {
        single: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                        noBottomPadding: true,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Daily Stickiness",
                    type: "double?",
                    format: "percentagesign",
                    yAxisMax: 1,
                    formatParameter: 0,
                    name: "DailyStickiness",
                    reversed: "False",
                },
            },
        },
        compare: {
            defaultType: "AppEngagementMetricsDashboardGraph",
            AppEngagementMetricsDashboardGraph: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        showLegend: false,
                        noBottomPadding: true,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Daily Stickiness",
                    type: "double?",
                    format: "percentagesign",
                    yAxisMax: 1,
                    formatParameter: 0,
                    name: "DailyStickiness",
                    reversed: "False",
                },
            },
        },
    },
};
